import React, { useState, useEffect } from 'react';
import { X, Phone, ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const departments = [
  'Obstetrics & Gynaecology', 'Urology', 'Cardiology', 'Orthopaedics',
  'Gastroenterology', 'ENT', 'Paediatrics', 'Spine & Neurology', 'Diabetology', 'General Check-up',
];

const ExitIntentPopup = () => {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', department: '' });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed this session
    if (sessionStorage.getItem('exit_popup_dismissed')) {
      setDismissed(true);
      return;
    }

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !dismissed && !show) {
        setShow(true);
      }
    };

    // Also trigger after 45 seconds of inactivity on mobile
    let mobileTimer;
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      mobileTimer = setTimeout(() => {
        if (!dismissed && !sessionStorage.getItem('exit_popup_dismissed')) {
          setShow(true);
        }
      }, 45000);
    }

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, [dismissed, show]);

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    sessionStorage.setItem('exit_popup_dismissed', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.department) {
      toast.error('Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        name: formData.name,
        phone: formData.phone,
        email: `${formData.phone}@exitpopup.sakthi`,
        preferred_date: new Date().toISOString().split('T')[0],
        preferred_time: 'Callback Requested',
        department: formData.department,
        message: 'Exit Intent Popup - Free Consultation',
        language: 'en',
      });
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'form_submission', form_name: 'exit_intent_popup', form_department: formData.department, form_source: 'exit_popup' });
      }
      if (window.gtag) { window.gtag('event', 'conversion', { send_to: 'AW-17542370694/exit_popup' }); }
      setSuccess(true);
      setTimeout(() => { handleDismiss(); }, 3000);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="exit-intent-popup">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleDismiss}></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
        {/* Close button */}
        <button onClick={handleDismiss} className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors" data-testid="exit-popup-close">
          <X size={16} className="text-gray-500" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-[#0066b3] to-[#004a82] px-6 py-5 text-white">
          <h3 className="text-xl font-bold">Wait! Don't Leave Yet</h3>
          <p className="text-sm opacity-80 mt-1">Book a FREE consultation before you go</p>
        </div>

        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-emerald-600" />
            </div>
            <h4 className="text-lg font-bold text-[#0f172a]">Thank You!</h4>
            <p className="text-sm text-[#64748b] mt-1">Our team will call you shortly.</p>
          </div>
        ) : (
          <div className="p-6">
            {/* Trust points */}
            <div className="flex items-center gap-4 mb-5 text-xs text-[#5a6b7c]">
              <span className="flex items-center gap-1"><CheckCircle size={14} className="text-emerald-500" /> Free Consultation</span>
              <span className="flex items-center gap-1"><CheckCircle size={14} className="text-emerald-500" /> No Obligation</span>
              <span className="flex items-center gap-1"><CheckCircle size={14} className="text-emerald-500" /> Quick Callback</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <input type="hidden" id="zc_gad" name="zc_gad" value="" />
              <Input
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="h-12 border-gray-200" required data-testid="exit-popup-name"
              />
              <Input
                type="tel" placeholder="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="h-12 border-gray-200" required data-testid="exit-popup-phone"
              />
              <Select value={formData.department} onValueChange={(v) => setFormData({...formData, department: v})}>
                <SelectTrigger className="h-12 border-gray-200" data-testid="exit-popup-department">
                  <SelectValue placeholder="Select Department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="submit" disabled={loading} className="w-full h-12 bg-[#c41e3a] hover:bg-[#a0182f] text-white font-semibold" data-testid="exit-popup-submit">
                {loading ? <><Loader2 size={16} className="animate-spin mr-2" /> Booking...</> : <>Book Free Consultation <ArrowRight size={16} className="ml-2" /></>}
              </Button>
            </form>

            {/* Call alternative */}
            <div className="mt-4 text-center">
              <p className="text-xs text-[#94a3b8] mb-1">Or call us directly</p>
              <a href="tel:+914428442626" onClick={() => { if(window.dataLayer) window.dataLayer.push({event: 'click_to_call', call_source: 'exit_popup'}); }} className="inline-flex items-center gap-2 text-[#0066b3] font-semibold text-sm hover:underline">
                <Phone size={14} /> +91 44 2844 2626
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExitIntentPopup;
